import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "commons/store/store";
import { CommentCommentCommentReadIdReadContentReadStatusReadOwnerReadUpdatedReadCreatedRead as CurrentData } from "types/api.types";
import axiosSecureInstance from "commons/axios/axiosSecureInstance";
import { HalResource } from "types/halResource.types";

// @todo one url variable per module or from route list

export interface ResourcesState {
  comments: {
    itemsPerPage: number;
    totalItems: number;
    data: CurrentData[];
  };
  commentDetails: CurrentData | null;
  isLoading: boolean;
  error: any;
}

const initialState: ResourcesState = {
  comments: {
    itemsPerPage: 0,
    totalItems: 0,
    data: [],
  },
  commentDetails: null,
  isLoading: false,
  error: null
};

interface ResourceValues {
  name: string;
}

type fetchResourcesParams = {
  page: number;
};

export const fetchResources = createAsyncThunk(
  "comments/fetchResources",
  async ({ page }: fetchResourcesParams) => {
    const response = await axiosSecureInstance.get<
      HalResource<CurrentData[]>
    >("/api/comments", {
      headers: {
        accept: "application/hal+json",
      },
      params: {
        page,
        pagination: true,
      },
    });
    return response.data;
  }
);

export const fetchResource = createAsyncThunk(
  "comments/fetchResource",
  async (id: string) => {
    const response = await axiosSecureInstance.get<CurrentData>(
      `/api/comments/${id}`
    );
    return response.data;
  }
);

export const addResource = createAsyncThunk(
  "comments/addResource",
  async (values: ResourceValues, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.post(`/api/comments`, {
        ...values,
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

type editResourceParams = {
  id: string;
  values: ResourceValues;
};

export const editResource = createAsyncThunk(
  "comments/editResource",
  async ({ id, values }: editResourceParams, thunkAPI) => {
    try {
      const response = await axiosSecureInstance.put(
        `/api/comments/${id}`,
        {

        }
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const deleteResource = createAsyncThunk(
  "comments/deleteResource",
  async (id: string) => {
    await axiosSecureInstance.delete(`/api/comments/${id}`);
  }
);

export const exportFile = createAsyncThunk(
  "comments/exportFile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosSecureInstance.get(`/api/comments/export/xlsx`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['Content-Disposition'] || response.headers['contentDisposition'];

      const now = new Date();
      const formattedDate = now.toISOString().replace(/\D/g, "").slice(0, 14);

      let fileName = `queries_${formattedDate}.xlsx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match.length > 1) {
          fileName = match[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      return "File downloaded successfully";
    } catch (error: any) {
      console.error('Error exporting file:', error);
      return rejectWithValue(error.response?.data || "Failed to export file");
    }
  }
);

export const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchResources.fulfilled, (state, action) => {
      state.comments.data = action.payload._embedded?.item || [];
      state.comments.itemsPerPage = action.payload.itemsPerPage;
      state.comments.totalItems = action.payload.totalItems;
    });
    builder.addCase(fetchResource.fulfilled, (state, action) => {
      state.commentDetails = action.payload;
    });
    builder
      .addCase(exportFile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(exportFile.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(exportFile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const selectResources = (state: RootState) => state.comments.comments.comments;
export const selectResourceDetails = (state: RootState) => state.comments.comments.commentDetails;
export const selectResourceLoading = (state: RootState) => state.comments.comments.isLoading;

export default commentsSlice.reducer;
